import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Building2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}!`);
      navigate(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="text-center mb-8 fade-up">
          <div className="h-12 w-12 rounded-xl bg-emerald-400 flex items-center justify-center mx-auto mb-4 shadow-[0_0_20px_rgba(0,229,153,0.4)]">
            <Building2 className="h-6 w-6 text-slate-950" strokeWidth={2.5} />
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Agent Login</h1>
          <p className="text-slate-400 mt-2 text-sm">Access your exclusive lead pipeline</p>
        </div>
        <Card className="bg-slate-900/90 border-slate-800 rounded-2xl p-8 backdrop-blur-xl">
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="text-slate-400">Email</Label>
              <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-slate-950 border-slate-800 mt-1.5" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-slate-400">Password</Label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-slate-950 border-slate-800 mt-1.5" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full h-11">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">
            New agent?{" "}
            <Link to="/register" className="text-emerald-400 hover:underline" data-testid="login-to-register-link">
              Create an account
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
